import React, { useEffect, useMemo, useState } from 'react';
import { ConfidenceStrip } from '../../components/journeys/ConfidenceStrip';
import { DistributionSummary } from '../../components/journeys/DistributionSummary';
import { IncompleteJourneyCallout } from '../../components/journeys/IncompleteJourneyCallout';
import { JourneyFilterBar } from '../../components/journeys/JourneyFilterBar';
import { PathInsightPanel } from '../../components/journeys/PathInsightPanel';
import { PathList } from '../../components/journeys/PathList';
import { PrivacyNote } from '../../components/journeys/PrivacyNote';
import { PanelStateMessage } from '../../components/dashboard/PanelStateMessage';
import { fetchJourneyFilters, fetchPaths } from '../../api/journeys';
import { useAuth } from '../../context/AuthContext';
import { useTenantView } from '../../context/TenantViewContext';
import { JOURNEY_FILTER_OPTIONS, PATHS_MOCK } from '../../data/journeysMock';
import { usePersistedState } from '../../hooks/usePersistedState';
import { buildJourneyApiFilters, LIVE_DATA_SOURCE_LABEL } from '../../lib/journeyApiFilters';
import { mapPathsApiToView } from '../../lib/mapPathsApi';

const DEFAULT_FILTERS = {
  season: 'all',
  venue: 'all',
  event: 'all',
  minShare: '0.01',
};

const PATHS_PREFS_KEY = 'vi.paths.prefs.v1';
const ALL_VENUE = { value: 'all', label: 'Venue: all' };
const ALL_EVENT = { value: 'all', label: 'Event: all' };

function mapFilterOptions(items, kind) {
  return (items || []).map((item) => ({
    value: String(item.value),
    label: kind === 'venue' ? `Venue: ${item.label}` : `Event: ${item.label}`,
  }));
}

export function PathsPage() {
  const { isSuperAdmin } = useAuth();
  const { viewingTenant } = useTenantView();
  const accountId = isSuperAdmin && viewingTenant?.id ? viewingTenant.id : null;

  const [storedFilters, setStoredFilters] = usePersistedState(PATHS_PREFS_KEY, DEFAULT_FILTERS);
  const appliedFilters = { ...DEFAULT_FILTERS, ...storedFilters };
  const setAppliedFilters = setStoredFilters;
  const [draftFilters, setDraftFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
    ...storedFilters,
  }));
  const [selectedPathId, setSelectedPathId] = useState(null);
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMock, setUsingMock] = useState(false);
  const [venueOptions, setVenueOptions] = useState([ALL_VENUE, ...JOURNEY_FILTER_OPTIONS.venues.slice(1)]);
  const [eventOptions, setEventOptions] = useState([ALL_EVENT, ...JOURNEY_FILTER_OPTIONS.events.slice(1)]);

  const filterFields = useMemo(
    () => [
      {
        key: 'season',
        label: 'Season',
        options: [
          { value: 'all', label: 'All seeded dates' },
          ...JOURNEY_FILTER_OPTIONS.seasons,
        ],
      },
      { key: 'venue', label: 'Venue', options: venueOptions },
      { key: 'event', label: 'Event', options: eventOptions },
      { key: 'minShare', label: 'Min. share', options: JOURNEY_FILTER_OPTIONS.minShares },
    ],
    [venueOptions, eventOptions]
  );

  useEffect(() => {
    let cancelled = false;
    async function loadFilters() {
      try {
        const data = await fetchJourneyFilters({ accountId });
        if (cancelled) return;
        setVenueOptions([ALL_VENUE, ...mapFilterOptions(data.venues, 'venue')]);
        setEventOptions([ALL_EVENT, ...mapFilterOptions(data.events, 'event')]);
      } catch {
        // Keep mock options on failure.
      }
    }
    loadFilters();
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const filters = buildJourneyApiFilters({
          season: appliedFilters.season,
          venue: appliedFilters.venue,
          event: appliedFilters.event,
          accountId,
        });
        const api = await fetchPaths(filters);
        if (cancelled) return;
        const mapped = mapPathsApiToView(api);
        setLiveData(mapped);
        setUsingMock(false);
        setSelectedPathId((current) => current || mapped.paths[0]?.id || null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Failed to load paths');
        setLiveData(null);
        setUsingMock(true);
        setSelectedPathId(PATHS_MOCK.paths[0]?.id || null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [
    appliedFilters.season,
    appliedFilters.venue,
    appliedFilters.event,
    accountId,
  ]);

  const data = !usingMock && liveData ? liveData : PATHS_MOCK;
  const minShare = Number(appliedFilters.minShare) || 0;
  const visiblePaths = (data.paths || []).filter((path) => path.share >= minShare);
  const selectedPath =
    visiblePaths.find((path) => path.id === selectedPathId) || visiblePaths[0] || null;

  function handleFilterChange(key, value) {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleApply() {
    setAppliedFilters(draftFilters);
    setSelectedPathId(null);
  }

  const dataSourceLabel = !usingMock
    ? LIVE_DATA_SOURCE_LABEL
    : `Demo · mock fallback${error ? ` · ${error}` : ''}`;

  return (
    <section className="flex w-full min-w-0 flex-col gap-4">
      <JourneyFilterBar
        fields={filterFields}
        values={draftFilters}
        onChange={handleFilterChange}
        onApply={handleApply}
        dataSourceLabel={dataSourceLabel}
      />

      <ConfidenceStrip tone={data.confidence.tone} title={data.confidence.title} />

      {loading ? (
        <PanelStateMessage
          variant="loading"
          title="Loading paths"
          message="Reading aggregate journey paths from the API."
        />
      ) : (
        <div className="grid min-w-0 items-start gap-4 xl:grid-cols-[minmax(0,1.75fr)_minmax(240px,0.85fr)]">
          <PathList
            title={data.listMeta.title}
            paths={visiblePaths}
            selectedPathId={selectedPath?.id}
            onSelectPath={setSelectedPathId}
          />

          <div className="flex min-w-0 flex-col gap-3">
            <PathInsightPanel path={selectedPath} />
            <IncompleteJourneyCallout
              title={data.incomplete.title}
              headline={data.incomplete.headline}
              action={data.incomplete.action}
            />
            <DistributionSummary items={data.distribution} />
          </div>
        </div>
      )}

      <PrivacyNote />
    </section>
  );
}
